import { IconButton, Typography, Grid, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { gridAPI } from 'api/grid';
import { CustomCellProps, TransformedDataObject } from 'components/common/grid/';
import IconMi from 'components/common/icon/IconMi';
import useBundleTranslation from 'i18n';
import AsyncPopup from 'components/common/popup/AsyncPopup';
import {
    adjustDeleteConfirmationPopupMessage,
    adjustDeleteConfirmationPopupSettings,
} from 'components/common/grid/buttons/MassDeleteButton';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';

export default function DeleteCell({
    href,
    confirmation,
    reloadGridData,
    t,
    confirmationUrl,
    remove,
    icon,
    data,
    gridEntityType,
    setGridDataChanged,
}: CustomCellProps) {
    if (undefined === t)
        t = useBundleTranslation(['main', 'components/common/grid/grid', 'components/common/form/form'])['t'];
    const tMain = useBundleTranslation('main')['t'];
    const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
    const [openAsyncConfirmation, setOpenAsyncConfirmation] = useState<boolean>(false);
    const [loadingMaskShow, setLoadingMaskShow] = useState<boolean>(false);

    const onClick = () => {
        if (confirmationUrl && confirmationUrl > '') {
            setOpenAsyncConfirmation(true);
            return;
        }

        setOpenConfirmation(true);
    };

    const itemName = useMemo(() => {
        const deleteColumnName = (data?.delete as TransformedDataObject)?.config?.deleteColumnName;
        const name = data ? ((data[deleteColumnName || ''] || data['name'] || data['display_name']) ?? '') : '';

        if (typeof name === 'string') {
            return name;
        }

        return name.config.text;
    }, [data]);

    const IGNORE_GRID_ENTITY_TYPE = 'ignore-grid-entity-type';
    const confirmationSettings = { ...(confirmation ?? {}) };

    if (gridEntityType && !confirmationSettings.entityType) {
        confirmationSettings.entityType = gridEntityType;
    }
    if (confirmationSettings.entityType === IGNORE_GRID_ENTITY_TYPE) {
        delete confirmationSettings.entityType;
    }
    if (!confirmationSettings?.text && confirmationSettings?.entityType) {
        confirmationSettings.text = 'delete_confirmation_popup.text.this_undone_entity_item';
    }

    const popupSettings: PopupSettings = adjustDeleteConfirmationPopupSettings({
        confirmation: confirmationSettings,
        t: t,
        isRemove: remove,
    });
    const confirmationMessage = adjustDeleteConfirmationPopupMessage({
        confirmation: confirmationSettings,
        t: t,
        isRemove: remove,
        currentItemName: itemName,
    });

    const makeDelete = () => {
        setLoadingMaskShow(true);
        if (href) {
            gridAPI
                .delete(href)
                .then((response) => {
                    if (response.data.status == 'ERROR') {
                        alert(response.data?.message ?? response.data.messages.join(' '));
                    } else {
                        if (setGridDataChanged) {
                            setGridDataChanged(true);
                        }
                        if (reloadGridData) {
                            reloadGridData();
                        }
                    }
                })
                .finally(() => {
                    setOpenConfirmation(false);
                    setOpenAsyncConfirmation(false);
                    setLoadingMaskShow(false);
                });
        }
    };

    return (
        <>
            <Grid container sx={{ height: 1, alignItems: 'center', justifyContent: 'end' }}>
                <Tooltip title={tMain(remove ? 'remove' : 'delete')}>
                    <IconButton aria-label="delete" size={'small'} onClick={onClick} sx={{ color: 'text.primary' }}>
                        <IconMi icon={icon ? icon.value : remove ? 'times' : 'delete'} fontSize={'16'} />
                    </IconButton>
                </Tooltip>
                {openConfirmation && (
                    <Popup
                        settings={popupSettings}
                        open={openConfirmation}
                        t={t}
                        onHide={() => setOpenConfirmation(false)}
                        onConfirm={makeDelete}
                    >
                        <Typography>{confirmationMessage}</Typography>
                        <LoadingPlaceholder open={loadingMaskShow} />
                    </Popup>
                )}
                {openAsyncConfirmation && confirmationUrl && (
                    <AsyncPopup
                        settingsUrl={confirmationUrl}
                        onHide={() => setOpenAsyncConfirmation(false)}
                        onConfirm={makeDelete}
                        popupSettings={popupSettings}
                    />
                )}
            </Grid>
        </>
    );
}
